import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import TimerRoundedIcon from '@mui/icons-material/TimerRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import LocalShippingRoundedIcon from '@mui/icons-material/LocalShippingRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { requestJson } from '../lib/api';
import { useMenuPage } from '../hooks/useMenuPage';

const delayRows = [
  ['immediate', 'Immédiat'],
  ['oneDay', '1 jour ouvré'],
  ['twoDays', '2 jours ouvrés'],
  ['threeDays', '3 jours ouvrés'],
  ['fourPlusDays', '+4 jours ouvrés'],
  ['missingStart', 'Donnée de départ manquante'],
];

function previousMonthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  // Date inputs represent calendar days in the user's local timezone. Using
  // toISOString() here shifts the first/last day back one day for timezones
  // east of UTC (for example 01/08 becomes 31/07). Format the local calendar
  // date explicitly so the default previous-month range remains exact.
  const formatInputDate = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
  return { from: formatInputDate(first), to: formatInputDate(last) };
}

function formatPeriod(period) {
  if (!period) return '';
  const [year, month] = period.split('-');
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('fr-FR', {
    month: 'short',
    year: 'numeric',
  });
}

function formatInputDate(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-');
}

function rangeForMonth(period) {
  if (!period) return { from: '', to: '' };
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return { from: '', to: '' };
  return {
    from: formatInputDate(new Date(year, month - 1, 1)),
    to: formatInputDate(new Date(year, month, 0)),
  };
}

function csvEscape(value) {
  return `"${String(value ?? '').replace(/"/g, '""')}"`;
}

function downloadCsv(filename, rows) {
  const blob = new Blob([`\uFEFF${rows.map((row) => row.map(csvEscape).join(';')).join('\n')}`], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function formatPercent(value) {
  return `${Number(value || 0).toFixed(2).replace('.', ',')} %`;
}

function cumulativeDelayRate(summary, keys) {
  const data = summary || {};
  if (!data.total) return 0;
  return (keys.reduce((total, key) => total + Number(data[key] || 0), 0) / data.total) * 100;
}

function DelayTable({ title, summary, startIcon, endIcon }) {
  const data = summary || {};
  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          {startIcon}
          <Typography variant="h6" sx={{ fontWeight: 900 }}>{title}</Typography>
          {endIcon}
        </Stack>
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Délai ouvré</TableCell>
                <TableCell align="right">Dossiers</TableCell>
                <TableCell align="right">Part du total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {delayRows.map(([key, label]) => (
                <TableRow key={key}>
                  <TableCell>{label}</TableCell>
                  <TableCell align="right">{data[key] || 0}</TableCell>
                  <TableCell align="right">
                    {data.total ? `${(((data[key] || 0) / data.total) * 100).toFixed(1)} %` : '0,0 %'}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 900 } }}>
                <TableCell>Total / moyenne</TableCell>
                <TableCell align="right">{data.total || 0}</TableCell>
                <TableCell align="right">{Number(data.averageBusinessDays || 0).toFixed(2)} jours</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Taux ≤ 1 jour ouvré</TableCell>
                <TableCell align="right" colSpan={2}>{formatPercent(cumulativeDelayRate(data, ['immediate', 'oneDay']))}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Taux ≤ 2 jours ouvrés</TableCell>
                <TableCell align="right" colSpan={2}>{formatPercent(cumulativeDelayRate(data, ['immediate', 'oneDay', 'twoDays']))}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

const spreadsheetTypeOrder = [
  'Classique',
  'Easy-Digifit',
  'N.C.A.',
  'Percées',
  'T.D.',
  'Traçabilité',
  'V.S.',
  'Clip',
];

function spreadsheetPeriodLabel(period) {
  if (!period) return '';
  const [year, month] = period.split('-');
  return `${month}-${year}`;
}

function SpreadsheetSummary({ stats, periods, typeNames }) {
  const types = [
    ...spreadsheetTypeOrder,
    ...typeNames.filter((type) => !spreadsheetTypeOrder.includes(type)).sort((a, b) => a.localeCompare(b)),
  ];
  const typeValue = (period, type) => {
    if (type === 'N.C.A.') return period.quality?.nca || 0;
    if (type === 'Traçabilité') return period.quality?.traceability || 0;
    return period.types?.[type] || 0;
  };
  const globalTypeValue = (type) => {
    if (type === 'N.C.A.') return stats.nca?.count || 0;
    if (type === 'Traçabilité') return stats.traceability?.count || 0;
    return stats.types?.find((item) => item.type === type)?.count || 0;
  };
  const volumeFor = (period) => Object.values(period.types || {}).reduce((sum, value) => sum + Number(value || 0), 0);
  const qualityBaseFor = (period) => Number(period.qualityBaseVolume || 0);
  const conformingWithoutVsFor = (period) => Number(period.conformingVolumeExcludingVs || 0);

  const delayValue = (period, metric, key) => period.delays?.[metric]?.[key] || 0;
  const delayTotal = (metric, key) => stats.delays?.[metric]?.[key] || 0;
  const delayRate = (period, metric, keys) => cumulativeDelayRate(period.delays?.[metric], keys);
  const globalDelayRate = (metric, keys) => cumulativeDelayRate(stats.delays?.[metric], keys);

  const cellSx = { whiteSpace: 'nowrap', textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
  const labelSx = { fontWeight: 700, minWidth: 230, whiteSpace: 'nowrap' };
  const sectionSx = { backgroundColor: 'rgba(255, 235, 59, 0.28)', fontWeight: 900 };
  const qualitySx = { backgroundColor: 'rgba(255, 193, 7, 0.12)' };
  const totalSx = { backgroundColor: 'rgba(96, 125, 139, 0.12)', '& td': { fontWeight: 900 } };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 900, mb: 0.5 }}>
          Récapitulatif mensuel
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 680 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ minWidth: 230, fontWeight: 900 }}>Type / indicateur</TableCell>
                {periods.map((period) => (
                  <TableCell key={period.period} align="right" sx={{ minWidth: 100, fontWeight: 900 }}>
                    {spreadsheetPeriodLabel(period.period)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ minWidth: 100, fontWeight: 900 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell colSpan={periods.length + 2} sx={sectionSx}>MONTAGES PAR TYPE</TableCell>
              </TableRow>
              {types.map((type) => (
                <TableRow key={`sheet-type-${type}`}>
                  <TableCell sx={labelSx}>{type}</TableCell>
                  {periods.map((period) => <TableCell key={`${type}-${period.period}`} sx={cellSx}>{typeValue(period, type)}</TableCell>)}
                  <TableCell sx={cellSx}>{globalTypeValue(type)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Total général montages conformes (V.S. inclus)</TableCell>
                {periods.map((period) => <TableCell key={`conforming-${period.period}`} sx={cellSx}>{volumeFor(period)}</TableCell>)}
                <TableCell sx={cellSx}>{stats.conformingVolume || 0}</TableCell>
              </TableRow>
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Total hors V.S. et traçabilité</TableCell>
                {periods.map((period) => <TableCell key={`quality-base-${period.period}`} sx={cellSx}>{qualityBaseFor(period)}</TableCell>)}
                <TableCell sx={cellSx}>{stats.qualityBaseVolume || 0}</TableCell>
              </TableRow>
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Montages conformes hors V.S.</TableCell>
                {periods.map((period) => <TableCell key={`conforming-no-vs-${period.period}`} sx={cellSx}>{conformingWithoutVsFor(period)}</TableCell>)}
                <TableCell sx={cellSx}>{stats.conformingVolumeExcludingVs || 0}</TableCell>
              </TableRow>
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Volume expédié avant qualité</TableCell>
                {periods.map((period) => <TableCell key={`volume-${period.period}`} sx={cellSx}>{period.volume || 0}</TableCell>)}
                <TableCell sx={cellSx}>{stats.volume || 0}</TableCell>
              </TableRow>
              <TableRow sx={qualitySx}>
                <TableCell sx={labelSx}>Taux NCA</TableCell>
                {periods.map((period) => {
                  const volume = qualityBaseFor(period);
                  return <TableCell key={`nca-rate-${period.period}`} sx={cellSx}>{volume ? `${((period.quality?.nca || 0) / volume * 100).toFixed(2)} %` : '0,00 %'}</TableCell>;
                })}
                <TableCell sx={cellSx}>{Number(stats.nca?.rate || 0).toFixed(2)} %</TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={periods.length + 2} sx={{ ...sectionSx, mt: 1 }}>DÉLAIS — RÉCEPTION DES VERRES → EXPÉDITION MAGASIN</TableCell>
              </TableRow>
              {delayRows.map(([key, label]) => (
                <TableRow key={`sheet-reception-${key}`}>
                  <TableCell sx={labelSx}>{label}</TableCell>
                  {periods.map((period) => <TableCell key={`${key}-${period.period}`} sx={cellSx}>{delayValue(period, 'receptionToSend', key)}</TableCell>)}
                  <TableCell sx={cellSx}>{delayTotal('receptionToSend', key)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Total / moyenne (jours ouvrés)</TableCell>
                {periods.map((period) => <TableCell key={`reception-total-${period.period}`} sx={cellSx}>{period.delays?.receptionToSend?.total || 0} / {Number(period.delays?.receptionToSend?.averageBusinessDays || 0).toFixed(2)}</TableCell>)}
                <TableCell sx={cellSx}>{delayTotal('receptionToSend', 'total')} / {Number(stats.delays?.receptionToSend?.averageBusinessDays || 0).toFixed(2)}</TableCell>
              </TableRow>
              <TableRow sx={qualitySx}>
                <TableCell sx={labelSx}>Taux ≤ 1 jour ouvré</TableCell>
                {periods.map((period) => <TableCell key={`reception-rate-1-${period.period}`} sx={cellSx}>{formatPercent(delayRate(period, 'receptionToSend', ['immediate', 'oneDay']))}</TableCell>)}
                <TableCell sx={cellSx}>{formatPercent(globalDelayRate('receptionToSend', ['immediate', 'oneDay']))}</TableCell>
              </TableRow>
              <TableRow sx={qualitySx}>
                <TableCell sx={labelSx}>Taux ≤ 2 jours ouvrés</TableCell>
                {periods.map((period) => <TableCell key={`reception-rate-2-${period.period}`} sx={cellSx}>{formatPercent(delayRate(period, 'receptionToSend', ['immediate', 'oneDay', 'twoDays']))}</TableCell>)}
                <TableCell sx={cellSx}>{formatPercent(globalDelayRate('receptionToSend', ['immediate', 'oneDay', 'twoDays']))}</TableCell>
              </TableRow>

              <TableRow>
                <TableCell colSpan={periods.length + 2} sx={sectionSx}>DÉLAIS — RAPPROCHEMENT / MONTAGE → EXPÉDITION MAGASIN</TableCell>
              </TableRow>
              {delayRows.map(([key, label]) => (
                <TableRow key={`sheet-matching-${key}`}>
                  <TableCell sx={labelSx}>{label}</TableCell>
                  {periods.map((period) => <TableCell key={`matching-${key}-${period.period}`} sx={cellSx}>{delayValue(period, 'matchingToSend', key)}</TableCell>)}
                  <TableCell sx={cellSx}>{delayTotal('matchingToSend', key)}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={totalSx}>
                <TableCell sx={labelSx}>Total / moyenne (jours ouvrés)</TableCell>
                {periods.map((period) => <TableCell key={`matching-total-${period.period}`} sx={cellSx}>{period.delays?.matchingToSend?.total || 0} / {Number(period.delays?.matchingToSend?.averageBusinessDays || 0).toFixed(2)}</TableCell>)}
                <TableCell sx={cellSx}>{delayTotal('matchingToSend', 'total')} / {Number(stats.delays?.matchingToSend?.averageBusinessDays || 0).toFixed(2)}</TableCell>
              </TableRow>
              <TableRow sx={qualitySx}>
                <TableCell sx={labelSx}>Taux ≤ 1 jour ouvré</TableCell>
                {periods.map((period) => <TableCell key={`matching-rate-1-${period.period}`} sx={cellSx}>{formatPercent(delayRate(period, 'matchingToSend', ['immediate', 'oneDay']))}</TableCell>)}
                <TableCell sx={cellSx}>{formatPercent(globalDelayRate('matchingToSend', ['immediate', 'oneDay']))}</TableCell>
              </TableRow>
              <TableRow sx={qualitySx}>
                <TableCell sx={labelSx}>Taux ≤ 2 jours ouvrés</TableCell>
                {periods.map((period) => <TableCell key={`matching-rate-2-${period.period}`} sx={cellSx}>{formatPercent(delayRate(period, 'matchingToSend', ['immediate', 'oneDay', 'twoDays']))}</TableCell>)}
                <TableCell sx={cellSx}>{formatPercent(globalDelayRate('matchingToSend', ['immediate', 'oneDay', 'twoDays']))}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          La ligne « hors V.S. et traçabilité » sert de base aux ratios qualité : les V.S. et les montages non réalisés sont exclus, tandis que les NCA/NCM restent comptés comme indicateurs. Les délais et les taux cumulés utilisent le volume de référence « verres tracés hors traçabilité » (montages conformes + NCA), une seule fois par JOB ; les NCM et les traçabilités montage impossible/non sont exclus. « Donnée de départ manquante » identifie les JOB sans réception ou rapprochement horodaté exploitable. Pour les lignes « Total / moyenne », le format est <strong>nombre de dossiers / moyenne en jours ouvrés</strong> (la moyenne exclut les données manquantes). Aucune prime n'est calculée dans cette version.
        </Typography>
      </CardContent>
    </Card>
  );
}

export default function DelayStatisticsV2Page() {
  const menuPage = useMenuPage('/statistiques/delais-v2');
  const initialRange = useMemo(previousMonthRange, []);
  const [range, setRange] = useState(initialRange);
  const [selectedPeriod, setSelectedPeriod] = useState(() => initialRange.from.slice(0, 7));
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const periodOptions = useMemo(() => {
    const now = new Date();
    return Array.from({ length: 37 }, (_, index) => {
      const date = new Date(now.getFullYear(), now.getMonth() - (36 - index), 1);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    });
  }, []);

  const handlePeriodChange = (event) => {
    const period = event.target.value;
    setSelectedPeriod(period);
    if (period) setRange(rangeForMonth(period));
  };

  const loadStats = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setStats(await requestJson(`/statistics/delays/v2?${new URLSearchParams(range).toString()}`));
    } catch (err) {
      setStats(null);
      setError(err.message || 'Impossible de charger les statistiques.');
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => { loadStats(); }, [loadStats]);

  const periods = useMemo(() => stats?.monthly || [], [stats]);
  const typeNames = useMemo(() => {
    const names = new Set((stats?.types || []).map((item) => item.type));
    periods.forEach((period) => Object.keys(period.types || {}).forEach((type) => names.add(type)));
    return [...names];
  }, [periods, stats]);

  const spreadsheetTypeNames = useMemo(() => [
    ...spreadsheetTypeOrder,
    ...typeNames.filter((type) => !spreadsheetTypeOrder.includes(type)),
  ], [typeNames]);

  const handleExport = () => {
    if (!stats) return;
    const rows = [
      ['Période', ...typeNames],
      ...periods.map((period) => [period.period, ...typeNames.map((type) => period.types?.[type] || 0)]),
      [],
      ['Délai réception verres → envoi magasin', 'Immédiat', '1 jour', '2 jours', '3 jours', '+4 jours', 'Donnée manquante', 'Total'],
      ['Global', ...delayRows.map(([key]) => stats.delays.receptionToSend?.[key] || 0), stats.delays.receptionToSend?.total || 0],
      ['Taux ≤ 1 jour ouvré', formatPercent(cumulativeDelayRate(stats.delays.receptionToSend, ['immediate', 'oneDay']))],
      ['Taux ≤ 2 jours ouvrés', formatPercent(cumulativeDelayRate(stats.delays.receptionToSend, ['immediate', 'oneDay', 'twoDays']))],
      ['Délai rapprochement/montage → envoi magasin', 'Immédiat', '1 jour', '2 jours', '3 jours', '+4 jours', 'Donnée manquante', 'Total'],
      ['Global', ...delayRows.map(([key]) => stats.delays.matchingToSend?.[key] || 0), stats.delays.matchingToSend?.total || 0],
      ['Taux ≤ 1 jour ouvré', formatPercent(cumulativeDelayRate(stats.delays.matchingToSend, ['immediate', 'oneDay']))],
      ['Taux ≤ 2 jours ouvrés', formatPercent(cumulativeDelayRate(stats.delays.matchingToSend, ['immediate', 'oneDay', 'twoDays']))],
    ];
    downloadCsv(`stats-delais-v2-${range.from}-${range.to}.csv`, rows);
  };

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" spacing={2}>
        <Box>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 900 }}>{menuPage.sectionTitle}</Typography>
          <Typography variant="h4" sx={{ fontWeight: 900 }}>{menuPage.pageTitle}</Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'center' }}>
          <TextField
            select
            label="Période"
            value={selectedPeriod}
            onChange={handlePeriodChange}
            size="small"
            sx={{ minWidth: 150 }}
          >
            <MenuItem value=""><em>Personnalisée</em></MenuItem>
            {periodOptions.map((period) => <MenuItem key={period} value={period}>{formatPeriod(period)}</MenuItem>)}
          </TextField>
          <TextField type="date" label="Du" value={range.from} onChange={(e) => { setSelectedPeriod(''); setRange((r) => ({ ...r, from: e.target.value })); }} size="small" InputLabelProps={{ shrink: true }} />
          <TextField type="date" label="Au" value={range.to} onChange={(e) => { setSelectedPeriod(''); setRange((r) => ({ ...r, to: e.target.value })); }} size="small" InputLabelProps={{ shrink: true }} />
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadStats}>Rafraîchir</Button>
          <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={handleExport} disabled={!stats}>Export CSV</Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}><CircularProgress /></Box> : stats && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(5, 1fr)' }, gap: 2 }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent><BarChartRoundedIcon color="info" /><Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{stats.tracedVolumeExcludingTraceability ?? 0}</Typography><Typography color="text.secondary">Total verres tracés (hors traçabilité)</Typography></CardContent></Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent><TimerRoundedIcon color="primary" /><Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{stats.conformingVolume ?? stats.volume ?? 0}</Typography><Typography color="text.secondary">Montages conformes sur la période (V.S. inclus)</Typography></CardContent></Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent><WarningAmberRoundedIcon color="error" /><Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{stats.nca?.count || 0}</Typography><Typography color="text.secondary">NCA ({Number(stats.nca?.rate || 0).toFixed(2)} % du volume)</Typography></CardContent></Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent><FactCheckRoundedIcon color="warning" /><Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{stats.traceability?.count || 0}</Typography><Typography color="text.secondary">Traçabilité : montage impossible / non ({Number(stats.traceability?.rate || 0).toFixed(2)} %)</Typography></CardContent></Card>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}><CardContent><TimerRoundedIcon color="success" /><Typography variant="h4" sx={{ fontWeight: 900, mt: 1 }}>{stats.qualityBaseVolume ?? 0}</Typography><Typography color="text.secondary">Volume hors V.S. et traçabilité</Typography></CardContent></Card>
          </Box>

          <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
                <BarChartRoundedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 900 }}>Répartition mensuelle par type de montage</Typography>
              </Stack>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small"><TableHead><TableRow><TableCell>Type</TableCell>{periods.map((period) => <TableCell key={period.period} align="right">{formatPeriod(period.period)}</TableCell>)}<TableCell align="right">Total</TableCell></TableRow></TableHead>
                  <TableBody>{typeNames.map((type) => <TableRow key={type}><TableCell>{type}</TableCell>{periods.map((period) => <TableCell key={`${type}-${period.period}`} align="right">{period.types?.[type] || 0}</TableCell>)}<TableCell align="right">{stats.types?.find((item) => item.type === type)?.count || 0}</TableCell></TableRow>)}</TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          <Divider />
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2 }}>
            <DelayTable
              title="Réception des verres → expédition vers le magasin"
              summary={stats.delays?.receptionToSend}
              startIcon={<Inventory2RoundedIcon color="info" />}
              endIcon={<LocalShippingRoundedIcon color="primary" />}
            />
            <DelayTable
              title="Rapprochement / montage → expédition vers le magasin"
              summary={stats.delays?.matchingToSend}
              startIcon={<LinkRoundedIcon color="success" />}
              endIcon={<LocalShippingRoundedIcon color="primary" />}
            />
          </Box>

          <SpreadsheetSummary stats={stats} periods={periods} typeNames={spreadsheetTypeNames} />
        </>
      )}
    </Stack>
  );
}
